import type { Maybe } from '@graphql-tools/utils'
import type { AxiosError } from 'axios'
import uniq from 'lodash/uniq'

import configs from '@acre/config'

import request from '../requesters/default'
import {
  generateVerificationCacheKey,
  isVerificationCacheValid,
  reverifyCacheInvalidationKey,
  setVerificationCacheInvalidation,
} from '../utils/caseVerificationInvalidation'

const { API1_URL } = configs

type DipError = {
  detail?: {
    errors?: Array<{ message: string }>
  }
}

type submitDipProxy = SubmitDip & { _detail: DipDetails }

export const canDoDip = async (input: DipInput) => {
  if (!input.mortgage_ids.length) {
    return []
  }

  // Variable to determine if we need to add ignore_cache param
  // DIP invalidation shares the same logic as verifications
  // Refer to comments in caseVerificationInvalidation.ts
  const key = generateVerificationCacheKey(input.case_id, true, false, false)
  const isCacheValid = isVerificationCacheValid(key)

  const params: Record<string, any> = {
    case_id: input.case_id,
    mortgage_id: input.mortgage_ids,
    existing_mortgage_id: input.existing_mortgage_id,
  }

  // Add ignore_cache if the cache is invalid
  if (!isCacheValid) {
    params.ignore_cache = true
  }

  const response = await request.get<Dip[]>('/mortgage-application/can_do_dip', {
    baseURL: `${API1_URL}/acre`,
    params,
  })

  const hasVerificationsBeenCalled = Boolean(
    response?.headers?.['x-verifications-called'] || response.data?.some((dip) => dip.dip_available === true),
  )

  // Reset the invalidation key after using, after the request to avoid race conditions
  if (!isCacheValid && hasVerificationsBeenCalled) {
    reverifyCacheInvalidationKey(key)
  }

  return response.data
}

export const canDoDipNoProperty = async (input: NoPropertyDipInput) => {
  // Variable to determine if we need to add ignore_cache param
  // DIP invalidation shares the same logic as verifications
  // Refer to comments in caseVerificationInvalidation.ts
  const key = generateVerificationCacheKey(input.case_id, true, false, false)
  const isCacheValid = isVerificationCacheValid(key)

  const params: Record<string, any> = {
    case_id: input.case_id,
    mortgage_id: input.mortgage_id,
    existing_mortgage_id: input.existing_mortgage_id,
    lender: input.lender,
  }

  // Add ignore_cache if the cache is invalid
  if (!isCacheValid) {
    params.ignore_cache = true
  }

  const response = await request.get<Dip>('/mortgage-application/canDoDipNoProperty', {
    baseURL: `${API1_URL}/acre`,
    params,
  })

  const hasVerificationsBeenCalled = Boolean(
    response?.headers?.['x-verifications-called'] || response.data?.dip_available,
  )

  // Reset the invalidation key after using, after the request to avoid race conditions
  if (!isCacheValid && hasVerificationsBeenCalled) {
    reverifyCacheInvalidationKey(key)
  }

  return response.data
}

export const submitDip = async <T extends SubmitDip | SubmitLbgDip | null = SubmitDip>(
  payloadInput: DipInput,
  queryInput?: SubmitDipInput | LbgDipInput | null,
): Promise<T> => {
  if (payloadInput.mortgage_ids.length !== 1) {
    throw new Error('You must only try to DIP one mortgage at a time')
  }

  const payload = {
    case_id: payloadInput.case_id,
    mortgage_id: payloadInput.mortgage_ids[0],
    existing_mortgage_id: payloadInput.existing_mortgage_id,
  }

  const response = await request
    .post<submitDipProxy>('/mortgage-application/submitDip', payload, {
      baseURL: `${API1_URL}/acre`,
      params: queryInput,
    })
    .catch((res) => {
      // this block handles the detailed errors we sometimes get back from providers
      const e = res as AxiosError<DipError>
      if (e.isAxiosError) {
        const errors = e.response?.data?.detail?.errors
        if (errors?.length) {
          throw new Error(uniq(errors.map((err) => err.message)).join(' '))
        }
      }
      throw res
    })

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  if (!response?.data) {
    throw new Error('No data returned from DIP submission')
  }

  const { _detail, ...rest } = response.data

  const data = rest

  if (_detail) {
    data.details = _detail
  }

  return data as T
}

export const submitNoPropertyDip = async (payloadInput: NoPropertyDipInput, queryInput?: SubmitDipInput | null) => {
  const payload = {
    case_id: payloadInput.case_id,
    mortgage_id: payloadInput.mortgage_id,
    existing_mortgage_id: payloadInput.existing_mortgage_id,
  }

  const response = await request
    .post<submitDipProxy>('/mortgage-application/submitDipNoProperty', payload, {
      baseURL: `${API1_URL}/acre`,
      params: { ...queryInput, lender: payloadInput.lender },
    })
    .catch((res) => {
      // this block handles the detailed errors we sometimes get back from providers
      const e = res as AxiosError<DipError>
      if (e.isAxiosError) {
        const errors = e.response?.data?.detail?.errors
        if (errors?.length) {
          throw new Error(uniq(errors.map((err) => err.message)).join(' '))
        }
      }
      throw res
    })

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  const data = response?.data

  if (data?._detail) {
    data.details = data._detail
  }

  return data as SubmitDip
}

export const getDipStatus = async (input: DipStatusInput) => {
  const response = await request.get<submitDipProxy>('/mortgage-application/status', {
    baseURL: `${API1_URL}/acre`,
    params: {
      id: input.dip_application_id,
      mortgage: input.mortgage_id,
      case: input.case_id,
    },
  })

  const data = response?.data

  if (data?._detail) {
    data.details = data._detail
  }

  return data as SubmitDip
}

export const applyForDip = async (id: string, mortgage: string, reservation?: Maybe<boolean>) => {
  const response = await request.get<ApplyForDip>('/mortgage-application/fma', {
    baseURL: `${API1_URL}/acre`,
    params: {
      id,
      mortgage,
      reservation,
    },
  })

  return response.data
}

// @deprecated use getDoc
export const getDipCert = async (id: string, caseId: string, mortgageId: Maybe<string>) => {
  await request.get<DipError>('/mortgage-application/cert', {
    baseURL: `${API1_URL}/acre`,
    params: {
      id,
      mortgage: mortgageId,
      case: caseId,
    },
  })

  return true
}

export const getDoc = async (id: string, caseId: string, mortgageId: Maybe<string>, documentType: DipDocumentType) => {
  await request.get<DipError>('/mortgage-application/document', {
    baseURL: `${API1_URL}/acre`,
    params: {
      id,
      mortgage: mortgageId,
      case: caseId,
      document: documentType,
    },
  })
  return true
}

export const getFMAFields = async (caseId: string, mortgageId: string) => {
  const res = await request.get<FmaFieldsResponse>('/mortgage-application/fma/fields', {
    baseURL: `${API1_URL}/acre`,
    params: {
      mortgage_id: mortgageId,
      case_id: caseId,
    },
    validateStatus: (status) => {
      return status < 401
    },
  })
  return res.data
}

export const doFMA = async (input: FmaInput) => {
  type FmaResponseInternal = Omit<FmaInput, 'additional_fields'> & {
    additional_fields: Omit<FmaInput['additional_fields'], 'property_access_address'> & {
      property_access_address: string
    }
  }
  const mappedRequest: FmaResponseInternal = {
    ...input,
    additional_fields: {
      ...input.additional_fields,
      property_access_address: JSON.stringify(input.additional_fields?.property_access_address),
    },
  }
  const res = await request.post<FmaResponse>('/mortgage-application/fma/full', mappedRequest, {
    baseURL: `${API1_URL}/acre`,
    validateStatus: (status) => {
      return status < 401
    },
  })
  return res.data
}

export const doSolicitorSearch = async (q: string) => {
  const res = await request.get<Array<SolicitorSearchResult>>('/mortgage-application/nationwide/ref_data/solicitors', {
    baseURL: `${API1_URL}/acre`,
    params: {
      q,
    },
  })
  return res.data
}

export const getAcreDipCertBasedOnLenderDip = async (_case: string, mortgage: string) => {
  await request.get<DipError>('/local-sourcing/cert/dip', {
    baseURL: `${API1_URL}/acre`,
    params: {
      case: _case,
      mortgage,
    },
  })
  return true
}

export const getAcreDipCertBasedOnAffordability = async (_case: string) => {
  await request.get<DipError>('/local-sourcing/cert/affordability', {
    baseURL: `${API1_URL}/acre`,
    params: {
      case: _case,
    },
  })
  return true
}
